import { useSettings } from '../../hooks/queries'

/**
 * Datenschutzhinweise — kurz, förmlich, in der üblichen Gliederung.
 *
 * Aus dem Betrieb, in zwei Schritten. Zuerst: „Die Texte sind sperrig in einem
 * schlechten Deutsch." Dann, auf die Rückfrage nach der Richtung: „kürzer,
 * förmlicher, juristisch einwandfrei." Hier stand vorher eine erzählende
 * Fassung im Ton der übrigen Anwendung — die ist für eine Datenschutzerklärung
 * die falsche Gattung. Sie gliedert sich nach Art. 13 DSGVO: Verantwortlicher,
 * Zwecke und Rechtsgrundlagen, Empfänger, Speicherdauer, Rechte.
 *
 * **Die ausführliche Fassung bleibt daneben bestehen**
 * (`docs/datenschutz-leser.md`) und tut etwas anderes: sie begründet, warum es
 * die drei Ausnahmen gibt. Eine Erklärung ist keine Erklärung im Rechtssinn,
 * und umgekehrt.
 *
 * **Und sie sagt die Wahrheit über *diese* Installation**, nicht über die
 * Software im Allgemeinen: die Empfänger stehen nur da, wenn ihr Zugang
 * wirklich eingerichtet ist (`services` aus `/api/settings`). Ein fester Text
 * benennte sonst eine Übermittlung, die gar nicht stattfindet — bei einer
 * Datenschutzerklärung ist das kein Schönheitsfehler.
 *
 * **Die Rechtsgrundlagen sind die Einschätzung des Betreibers.** Die Anwendung
 * schreibt sie hin, weil sie ihre eigene Verarbeitung kennt; verantwortlich
 * dafür ist, wer betreibt.
 *
 * **Heute sieht die Kategorie nur die Verwaltung** (`SettingsPage`). Das ist
 * eine Stufe und keine Endfassung. Zum Aufmachen sind es zwei Griffe — die
 * Bedingung um die Kategorie fällt, und im Server wandert `operator` vor das
 * `if (!$istVerwaltung)`. `tests/BetreiberTest.php` hält die heutige Stufe fest
 * und schlägt dann fehl; das ist Absicht.
 */
export function Datenschutz() {
  const settings = useSettings()
  const dienste = settings.data?.services
  const apple = dienste?.place === true || dienste?.weather === true
  /*
   * **Solange die Antwort unterwegs ist, wird hier nichts behauptet.**
   *
   * Zwei der Empfänger stehen nur da, wenn ihr Zugang eingerichtet ist — und
   * ehe der Server das gesagt hat, sähe die Seite aus wie eine Installation,
   * in der sie abgeschaltet sind. Dasselbe gilt für den Verantwortlichen:
   * „noch nicht eingetragen" wäre in diesem Augenblick keine Auskunft,
   * sondern eine falsche.
   */
  const laedt = settings.data === undefined

  return (
    <>
      <Verantwortlicher
        betreiber={settings.data?.operator}
        darfEinrichten={settings.data?.ceiling !== undefined}
        laedt={laedt}
      />

      <div className="card card--seite">
        <h2 className="card__titel">Grundsatz</h2>
        <p className="card__lead" style={{ marginBottom: 0 }}>
          Antworten, Reaktionen und Lesebestätigungen sind ausschließlich für
          die Verfasserin oder den Verfasser des Reiseberichts sichtbar, nicht
          für die übrigen Empfänger. Diese Beschränkung ist Bestandteil der
          Anwendung und nicht einstellbar.
        </p>
      </div>

      <div className="card card--seite">
        <h2 className="card__titel">Verarbeitete Daten, Zwecke, Rechtsgrundlagen</h2>

        <p className="schublade">
          <span className="schublade__name">Bestandsdaten</span>
          <span className="schublade__text">
            E-Mail-Adresse, Anzeigename, Passwort (nur als nicht umkehrbarer
            Hashwert), Zeitpunkt der Registrierung. Zweck: Einrichtung und
            Führung des Nutzerkontos. Rechtsgrundlage: Art. 6 Abs. 1 lit. b
            DSGVO.
          </span>
        </p>
        <p className="schublade">
          <span className="schublade__name">Inhaltsdaten</span>
          <span className="schublade__text">
            Nachrichten, Fotos, Videos, Sprachnachrichten, GIFs, Reaktionen und
            Lesestand nebst Zeitstempel. Zweck: Erbringung der Dienstleistung.
            Rechtsgrundlage: Art. 6 Abs. 1 lit. b DSGVO.
          </span>
        </p>
        <p className="schublade">
          <span className="schublade__name">Standortdaten</span>
          <span className="schublade__text">
            Ausschließlich Routen und Ortsangaben, die die Verfasserin oder der
            Verfasser eines Reiseberichts selbst hochlädt. Von Empfängern wird
            kein Standort erhoben; eine Ortung im Hintergrund findet nicht
            statt.
          </span>
        </p>
        <p className="schublade">
          <span className="schublade__name">Nutzungsdaten</span>
          <span className="schublade__text">
            Ein technisch notwendiges Cookie zur Anmeldung sowie eine
            Kurzangabe zum verwendeten Browser. Rechtsgrundlage: Art. 6 Abs. 1
            lit. b DSGVO, § 25 Abs. 2 Nr. 2 TDDDG. Cookies zu Werbe- oder
            Analysezwecken werden nicht eingesetzt.
          </span>
        </p>
        <p className="schublade">
          <span className="schublade__name">Sicherheitsdaten</span>
          <span className="schublade__text">
            Zahl fehlgeschlagener Anmeldeversuche. Zweck: Schutz vor
            unbefugtem Zugriff. Rechtsgrundlage: Art. 6 Abs. 1 lit. f DSGVO.
          </span>
        </p>
        <p className="schublade">
          <span className="schublade__name">Push-Benachrichtigungen</span>
          <span className="schublade__text">
            Nur nach ausdrücklicher Freigabe: Zustelladresse des Endgeräts und
            zugehörige Schlüssel. Rechtsgrundlage: Art. 6 Abs. 1 lit. a DSGVO;
            die Einwilligung ist jederzeit mit Wirkung für die Zukunft
            widerrufbar.
          </span>
        </p>
      </div>

      <div className="card card--seite">
        <h2 className="card__titel">Empfänger und Drittlandübermittlung</h2>

        <p className="schublade">
          <span className="schublade__name">Kartendarstellung</span>
          <span className="schublade__text">
            Die Kartenausschnitte werden vom Endgerät unmittelbar bei
            OpenStreetMap bzw. OpenTopoMap abgerufen. Übermittelt werden dabei
            IP-Adresse, der angeforderte Kartenausschnitt und der Name dieser
            Seite; Letzterer wird von OpenStreetMap vorausgesetzt. Inhalts- und
            Bestandsdaten werden nicht übermittelt. Rechtsgrundlage: Art. 6
            Abs. 1 lit. f DSGVO.
          </span>
        </p>

        {laedt && (
          <p className="schublade">
            <span className="schublade__text">Einen Moment …</span>
          </p>
        )}

        {/* Nur, wo es wirklich läuft: eine benannte Übermittlung, die gar nicht
            stattfindet, wäre in einer Datenschutzerklärung ein Fehler. */}
        {apple && (
          <p className="schublade">
            <span className="schublade__name">Ortsname und Wetter</span>
            <span className="schublade__text">
              Zur Benennung eines Kartenpunkts und zur Angabe des Wetters ruft
              der Server Dienste von Apple ab (Maps Server API, WeatherKit).
              Übermittelt werden Koordinaten und Zeitpunkt des jeweiligen
              Punktes, einmalig je Punkt, sowie die IP-Adresse des Servers. Der
              Abruf erfolgt nicht durch das Endgerät der Empfänger und wird
              ausschließlich durch die verfassende Person ausgelöst. Eine
              Übermittlung in die USA ist dabei möglich. Rechtsgrundlage: Art. 6
              Abs. 1 lit. f DSGVO.
            </span>
          </p>
        )}

        {dienste?.gif === true && (
          <p className="schublade">
            <span className="schublade__name">GIFs in Antworten</span>
            <span className="schublade__text">
              Bei Nutzung der GIF-Suche übermittelt der Server den eingegebenen
              Suchbegriff an Giphy; die IP-Adresse des Endgeräts wird dabei
              nicht übermittelt. Die Vorschaubilder der Trefferliste ruft das
              Endgerät unmittelbar bei Giphy ab, solange die Auswahl geöffnet
              ist. Das ausgewählte GIF wird auf diesem Server gespeichert und
              von dort ausgeliefert; beim späteren Betrachten findet keine
              Übermittlung an Giphy statt. Eine Übermittlung in die USA ist
              möglich. Rechtsgrundlage: Art. 6 Abs. 1 lit. f DSGVO.
            </span>
          </p>
        )}

        <p className="schublade">
          <span className="schublade__name">Push-Zustellung</span>
          <span className="schublade__text">
            Freigegebene Benachrichtigungen werden über den Push-Dienst des
            jeweiligen Geräteherstellers zugestellt. Dieser erhält Kenntnis vom
            Zustellvorgang; der Inhalt ist zwischen Server und Endgerät
            verschlüsselt.
          </span>
        </p>

        <p className="dienst__hinweis">
          Eine Weitergabe personenbezogener Daten an weitere Dritte findet
          nicht statt. Auftragsverarbeiter ist der Anbieter des Webhostings.
        </p>
      </div>

      <div className="card card--seite">
        <h2 className="card__titel">Speicherdauer</h2>
        <ul className="datenschutz__punkte">
          <li>
            Bestands- und Inhaltsdaten: bis zur Löschung durch die betroffene
            Person oder bis zur Löschung des Kontos.
          </li>
          <li>
            Inhalte innerhalb eines Reiseberichts verbleiben beim Empfänger der
            Nachricht, bis dieser sie löscht.
          </li>
          <li>Anmeldedaten der Sitzung: bis zur Abmeldung oder zum Ablauf der Sitzung.</li>
          <li>
            Push-Zustelladressen: bis zum Widerruf oder bis der Push-Dienst die
            Zustellung dauerhaft ablehnt.
          </li>
        </ul>
      </div>

      <div className="card card--seite">
        <h2 className="card__titel">Rechte der betroffenen Person</h2>
        <p className="card__lead" style={{ marginBottom: 8 }}>
          Es bestehen die Rechte auf Auskunft (Art. 15 DSGVO), Berichtigung
          (Art. 16), Löschung (Art. 17), Einschränkung der Verarbeitung
          (Art. 18), Datenübertragbarkeit (Art. 20) sowie auf Widerspruch gegen
          Verarbeitungen auf Grundlage von Art. 6 Abs. 1 lit. f DSGVO (Art. 21).
        </p>
        <p className="card__lead" style={{ marginBottom: 0 }}>
          Eine erteilte Einwilligung kann jederzeit mit Wirkung für die Zukunft
          widerrufen werden (Art. 7 Abs. 3 DSGVO). Unabhängig davon besteht ein
          Beschwerderecht bei einer Datenschutz-Aufsichtsbehörde (Art. 77
          DSGVO). Anträge sind an den oben genannten Verantwortlichen zu
          richten.
        </p>
      </div>

      <div className="card card--seite" style={{ marginBottom: 0 }}>
        <h2 className="card__titel">Was nicht stattfindet</h2>
        <ul className="datenschutz__punkte">
          <li>Keine Werbung, kein Verkauf und keine Weitergabe an Dritte.</li>
          <li>Keine Reichweiten- oder Nutzungsanalyse, keine Zählpixel.</li>
          <li>Keine Auswertung der Inhalte, weder manuell noch automatisiert.</li>
          <li>Keine automatisierte Entscheidungsfindung und kein Profiling.</li>
        </ul>
      </div>
    </>
  )
}

/**
 * Der Verantwortliche — nach Art. 13 Abs. 1 lit. a DSGVO an erster Stelle.
 *
 * **Fehlt der Eintrag, steht das da**, an genau der Stelle, an der die Angabe
 * stünde. Ein leeres Feld wäre die schlechtere Auskunft: es sieht aus wie ein
 * Fehler der Anwendung, und der Leser sucht ihn.
 *
 * Der Kontakt wird **nicht verlinkt, sondern als Text gesetzt**. Was in der
 * `.env` steht, ist ein von Hand getippter Wert; daraus ein `href` zu bauen
 * hieße, fremden Text in ein Attribut zu setzen.
 */
function Verantwortlicher({
  betreiber,
  darfEinrichten,
  laedt,
}: {
  betreiber?: { name: string; address: string; contact: string }
  darfEinrichten: boolean
  /** Die Antwort ist noch unterwegs — dann steht hier weder das eine noch das andere. */
  laedt: boolean
}) {
  const vollstaendig =
    betreiber !== undefined
    && betreiber.name !== ''
    && betreiber.address !== ''
    && betreiber.contact !== ''

  return (
    <div className="card card--seite">
      <h2 className="card__titel">Verantwortlicher</h2>

      {laedt ? (
        <p className="card__lead" style={{ marginBottom: 0 }}>
          Einen Moment …
        </p>
      ) : vollstaendig ? (
        <>
          <p className="card__lead" style={{ marginBottom: 6 }}>
            {betreiber.name}
            <br />
            {betreiber.address}
            <br />
            {betreiber.contact}
          </p>
          <p className="dienst__hinweis">
            Verantwortlicher im Sinne des Art. 4 Nr. 7 DSGVO.
          </p>
        </>
      ) : (
        <>
          <p className="card__lead" style={{ marginBottom: 0 }}>
            Die Angaben zum Verantwortlichen sind noch nicht hinterlegt. Anfragen
            richten Sie bitte an die Person, von der Sie eingeladen wurden.
          </p>

          {/* Schlüsselnamen sind für den Betrieb, nicht für den Leser. Heute
              sieht die Kategorie ohnehin nur die Verwaltung -- die Bedingung
              steht trotzdem da, damit sie beim Aufmachen nicht mitgeht. */}
          {darfEinrichten && (
            <>
              <p className="dienst__hinweis">
                Einzutragen in der <code>.env</code> auf dem Server; ein Neustart
                ist nicht nötig, die Seite zeigt es nach dem nächsten Neuladen.
              </p>
              <ul className="dienst__zeilen">
                <li>OPERATOR_NAME</li>
                <li>OPERATOR_ADDRESS</li>
                <li>OPERATOR_CONTACT</li>
              </ul>
            </>
          )}
        </>
      )}
    </div>
  )
}
